import { useCallback, useEffect, useRef } from 'react'
import { useAppStore } from '@core/store'
import { useJanelaNaFrente } from '@core/layout/camadas'
import {
  idDaJanelaDePropriedades,
  type PropertiesPopup,
} from '@viewer/store/popupsSlice'
import PropertiesPanel from '@viewer/inspector/PropertiesPanel'

interface Props {
  popup: PropertiesPopup
}

const MIN_W = 300
const MIN_H = 240
const MAX_W = 900
const MAX_H = 1000

function FloatingPropertiesWindow({ popup }: Props) {
  const closePopup = useAppStore((s) => s.closePopup)
  const movePopup = useAppStore((s) => s.movePopup)
  const resizePopup = useAppStore((s) => s.resizePopup)
  const bringPopupToFront = useAppStore((s) => s.bringPopupToFront)
  const { z, trazer } = useJanelaNaFrente(idDaJanelaDePropriedades(popup.id))
  useEffect(() => {
    console.info(
      '[FloatingPropertiesWindow] mounted id=' +
        popup.id +
        ' at (' +
        popup.position.x +
        ',' +
        popup.position.y +
        ') for #' +
        popup.target.localId,
    )
    return () => {
      console.info('[FloatingPropertiesWindow] unmounted id=' + popup.id)
    }
    // Mount/unmount only: position changes are diagnostically noisy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const dragOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)
  const resizeOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    sizeStart: { w: number; h: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      bringPopupToFront(popup.id)
      dragOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { x: popup.position.x, y: popup.position.y },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [popup.id, popup.position.x, popup.position.y, bringPopupToFront],
  )

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      bringPopupToFront(popup.id)
      resizeOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        sizeStart: { w: popup.size.w, h: popup.size.h },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [popup.id, popup.size.w, popup.size.h, bringPopupToFront],
  )

  // Global pointer move/up handlers, registered while drag/resize
  // is active. Pointer capture takes care of receiving events even
  // outside the title/handle, but we still bind on window for safety.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (dragOriginRef.current) {
        const { pointerStart, posStart } = dragOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const x = Math.min(
          Math.max(0, posStart.x + dx),
          window.innerWidth - 80,
        )
        const y = Math.min(
          Math.max(0, posStart.y + dy),
          window.innerHeight - 40,
        )
        movePopup(popup.id, { x, y })
      }
      if (resizeOriginRef.current) {
        const { pointerStart, sizeStart } = resizeOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const w = Math.min(MAX_W, Math.max(MIN_W, sizeStart.w + dx))
        const h = Math.min(MAX_H, Math.max(MIN_H, sizeStart.h + dy))
        resizePopup(popup.id, { w, h })
      }
    }
    const onUp = () => {
      dragOriginRef.current = null
      resizeOriginRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [popup.id, movePopup, resizePopup])

  return (
    <div
      role="dialog"
      aria-label="Propriedades (janela flutuante)"
      className="fixed flex flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{
        left: popup.position.x,
        top: popup.position.y,
        width: popup.size.w,
        height: popup.size.h,
        zIndex: z,
      }}
      onPointerDownCapture={trazer}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)] select-none"
      >
        <span className="truncate">
          Propriedades · #{popup.target.localId}
        </span>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => closePopup(popup.id)}
          className="rounded px-1.5 py-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          ×
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <PropertiesPanel pinnedSelection={popup.target} />
      </div>
      <div
        onPointerDown={onResizeDown}
        className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize"
        style={{
          backgroundImage:
            'linear-gradient(135deg, transparent 50%, var(--color-text-muted) 50%)',
          opacity: 0.6,
        }}
        title="Redimensionar"
      />
    </div>
  )
}

export default FloatingPropertiesWindow
